// The per-answer trait breakdown that expands under a product row, shared by the shelf-test
// and ecommerce chart tables (their calcTraitTable implementations were identical apart from
// the label on the final column).

import { encodeLabel, formatAmounts, pluralize } from '@aytm/app/stats/lib/util/helpers'

// Palette length: trait-value colour indices cycle modulo this size.
const PALETTE_SIZE = 21

const SECOND_MODES = ['attention', 'time', 'timeToFirstClick']

const formatTraitValue = (value, viewMode, priceTemplate) => {
  if ('pAmount' === viewMode && priceTemplate) return priceTemplate.replace(/{{price}}/gi, value.toFixed(2))
  if (['pAmount', 'pVolume'].includes(viewMode)) return value.toFixed(2)

  const unit = value && SECOND_MODES.includes(viewMode) ? ` ${pluralize(value, 'second', 'seconds')}` : ''
  return `${formatAmounts({ amount: value }, 'amount', 1)}${unit}`
}

export const calcTraitTable = ({
  id,
  trait,
  traitName,
  traitValues,
  traitAnswer,
  viewMode,
  priceTemplate,
  responsesTitle,
  percentDecimals,
  productTotalPickedUp,
  answerPosition
}) => {
  // a breakdown shows the figures of the row it hangs under, so it takes the report precision too
  const share = value => value.toFixed(percentDecimals ?? 1)

  const cols = [
    {
      type: 'title',
      title: encodeLabel(
        (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      )
    }
  ]

  if ('totalPickedUp' === viewMode) {
    cols.push(
      { type: 'value', title: 'Purchased %' },
      { type: 'value', title: 'Picked up only %' },
      { type: 'value', title: 'Total picked up' }
    )
  } else if (!responsesTitle) {
    // A mode the caller heads with no title of its own is one it draws as a share rather than a
    // magnitude, so the breakdown follows it: the share names itself where the % marker would sit,
    // and the column heads no count slot.
    cols.push({ type: 'progress', title: '', percentLabel: '% of this answer', percentDecimals })
  } else {
    cols.push({ type: 'value', title: responsesTitle })
  }

  const rows = traitValues.map((v, i) => {
    const a = traitAnswer[v.id] || {}
    const cells = [{ title: v.name, colorIndex: (i % PALETTE_SIZE) + 1 }]

    if ('totalPickedUp' === viewMode) {
      cells.push(
        { value: share(a.percents || 0) },
        // each value's part of the product's picked-up-only count, so the values split the product
        { value: share(productTotalPickedUp ? ((a.totalPickedUp || 0) / productTotalPickedUp) * 100 : 0) },
        { value: (a.amount || 0) + (a.totalPickedUp || 0) }
      )
    } else if (!responsesTitle) {
      cells.push({ amount: a.amount || 0, percents: a.percents || 0 })
    } else {
      cells.push({ value: formatTraitValue(a[viewMode] || 0, viewMode, priceTemplate) })
    }

    // each value row names the segment the chart draws for it, so hovering either echoes the other
    return { cells, position: i + 1, ...(null == answerPosition ? {} : { pointId: `a${answerPosition}t${i + 1}` }) }
  })

  return { id: `${id}-traits`, cols, rows, classNames: ['child'] }
}
